import { useState, useEffect } from 'react';
import {
  ActionIcon,
  Button,
  Code,
  CopyButton,
  Divider,
  Group,
  Loader,
  Select,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { IconTrash } from '@tabler/icons-react';
import QRCode from 'react-qr-code';
import { useTelegramBotRepository } from '@ayocore/telegram/client/rpc';
import type { TelegramBot } from '@ayocore/telegram/schemas/telegram-bot.schema';
import {
  generateTelegramGroupLink,
  getThreadTelegramLinks,
  removeTelegramThreadLink,
} from '@ayocore/activity/client/rpc';

type ThreadLink = {
  id: number;
  telegram_chat_id: string;
  telegram_chat_title: string | null;
  bot_id: number;
};

type Props = {
  thread: { id: number; uuid: string };
};

/**
 * Контент модалки управления привязками треда к Telegram-группам (открывается через useModal).
 *
 * Позволяет:
 * - просматривать список уже привязанных групп;
 * - удалять привязку;
 * - получать ссылку и QR-код для добавления бота в новую группу.
 *
 * При одном активном боте — ссылка и QR генерируются автоматически при открытии.
 * При нескольких ботах — генерация запускается после выбора бота из списка.
 */
export function ActivityThreadTelegramModal({ thread }: Props) {
  const botRepo = useTelegramBotRepository();

  const [links, setLinks] = useState<ThreadLink[]>([]);
  const [bots, setBots] = useState<TelegramBot[]>([]);
  const [selectedBotId, setSelectedBotId] = useState<string | null>(null);
  const [generatedLink, setGeneratedLink] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [removingId, setRemovingId] = useState<number | null>(null);

  // Генерация ссылки по botId — вызывается и автоматически, и при смене бота в Select
  const handleGenerate = async (botId: number) => {
    setGenerating(true);
    setGeneratedLink(null);
    try {
      const { link } = await generateTelegramGroupLink({
        thread_id: thread.id,
        thread_uuid: thread.uuid,
        bot_id: botId,
      });
      setGeneratedLink(link);
    } finally {
      setGenerating(false);
    }
  };

  // Компонент монтируется при открытии модалки (useModal) — грузим данные сразу
  useEffect(() => {
    setGeneratedLink(null);

    const load = async () => {
      setLoading(true);
      try {
        const [fetchedLinks, fetchedBots] = await Promise.all([
          getThreadTelegramLinks({ thread_id: thread.id }),
          botRepo.fetch(),
        ]);

        setLinks(fetchedLinks as ThreadLink[]);
        const activeBots = (fetchedBots as TelegramBot[]).filter(b => b.is_active);
        setBots(activeBots);

        // Если бот один — сразу генерируем ссылку и QR без лишнего шага
        if (activeBots.length === 1) {
          const botId = activeBots[0].id;
          setSelectedBotId(String(botId));
          const { link } = await generateTelegramGroupLink({
            thread_id: thread.id,
            thread_uuid: thread.uuid,
            bot_id: botId,
          });
          setGeneratedLink(link);
        }
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, [thread.id]);

  const handleRemove = async (linkId: number) => {
    setRemovingId(linkId);
    try {
      await removeTelegramThreadLink({ id: linkId });
      setLinks(prev => prev.filter(l => l.id !== linkId));
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <Stack gap='md'>
      {loading ? (
        <Group justify='center' p='md'>
          <Loader size='sm' />
        </Group>
      ) : (
        <>
          {links.length > 0 && (
            <Stack gap='xs'>
              <Text size='sm' fw={500}>
                Привязанные группы:
              </Text>
              {links.map(link => (
                <Group key={link.id} justify='space-between'>
                  <Text size='sm'>
                    {link.telegram_chat_title ?? `Chat ${link.telegram_chat_id}`}
                  </Text>
                  <ActionIcon
                    variant='subtle'
                    color='red'
                    size='sm'
                    loading={removingId === link.id}
                    onClick={() => {
                      void handleRemove(link.id);
                    }}
                    aria-label='Удалить привязку'
                  >
                    <IconTrash size={14} />
                  </ActionIcon>
                </Group>
              ))}
              <Divider />
            </Stack>
          )}

          <Stack gap='xs'>
            <Text size='sm' fw={500}>
              Добавить группу:
            </Text>

            {bots.length === 0 && (
              <Text size='sm' c='dimmed'>
                Нет активных ботов.
              </Text>
            )}

            {bots.length > 1 && (
              <Select
                placeholder='Выберите бота'
                data={bots.map(b => ({ value: String(b.id), label: b.name }))}
                value={selectedBotId}
                onChange={value => {
                  if (!value) return;
                  setSelectedBotId(value);
                  void handleGenerate(Number(value));
                }}
              />
            )}
          </Stack>

          {generating && (
            <Group justify='center' p='sm'>
              <Loader size='sm' />
            </Group>
          )}

          {generatedLink && (
            <Stack gap='xs'>
              <Text size='sm' fw={500}>
                Ссылка для добавления бота в группу:
              </Text>
              <Code block>{generatedLink}</Code>
              <CopyButton value={generatedLink}>
                {({ copied, copy }) => (
                  <Tooltip label={copied ? 'Скопировано' : 'Копировать'} withArrow position='right'>
                    <Button variant='light' onClick={copy}>
                      {copied ? 'Скопировано' : 'Копировать ссылку'}
                    </Button>
                  </Tooltip>
                )}
              </CopyButton>
              <Group justify='center' pt='xs'>
                <QRCode value={generatedLink} size={180} />
              </Group>
              <Text size='xs' c='dimmed'>
                Откройте ссылку или отсканируйте QR — Telegram предложит добавить бота в группу.
                После добавления уведомления из треда начнут приходить туда.
              </Text>
            </Stack>
          )}
        </>
      )}
    </Stack>
  );
}
